import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { api, formatApiError } from "../lib/api";
import { track } from "../lib/analytics";
import { formatJOD } from "../components/ProductCard";
import { toast } from "sonner";
import { Tag, Check, X } from "lucide-react";

const CITY_KEYS = ["Amman", "Irbid", "Zarqa", "Aqaba", "Madaba"];

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { t, tCity } = useLang();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [coupon, setCoupon] = useState({ code: "", applied: null, error: "" });

  const [form, setForm] = useState({
    full_name: user && user !== false ? user.name || "" : "",
    phone: user && user !== false ? user.phone || "" : "",
    email: user && user !== false ? user.email || "" : "",
    address: "",
    city: "Amman",
    notes: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("installation");

  useEffect(() => {
    track("begin_checkout");
  }, []);

  useEffect(() => {
    if (user && user !== false) {
      api.get("/account/addresses").then((r) => {
        setSavedAddresses(r.data || []);
        if (r.data?.[0]) {
          setForm((prev) => ({ ...prev, ...r.data[0], notes: "" }));
        }
      }).catch(() => {});
    }
  }, [user]);

  // Wait until auth status is resolved. Guests (user === false) can still check out.
  if (user === null || user === undefined) return null;

  if (items.length === 0) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 text-center">
        <h1 className="font-display font-bold text-3xl">{t("cart.empty")}</h1>
        <Link to="/shop" className="mt-6 inline-block text-electric underline">{t("common.continueShopping")}</Link>
      </div>
    );
  }

  const applyCoupon = async () => {
    setCoupon({ ...coupon, error: "" });
    if (!coupon.code.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", {
        code: coupon.code.trim(),
        subtotal,
      });
      setCoupon({ code: data.code, applied: data, error: "" });
      toast.success(t("common.toast.couponApplied").replace("{code}", data.code));
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setCoupon({ ...coupon, applied: null, error: msg });
    }
  };

  const removeCoupon = () => setCoupon({ code: "", applied: null, error: "" });

  const discount = coupon.applied?.discount || 0;
  const total = Math.max(0, subtotal - discount);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          slug: i.slug,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image: i.image,
          variant_id: i.variant_id || null,
          variant_label: i.variant_label || null,
        })),
        shipping: {
          ...form,
          email: form.email || undefined,
        },
        payment_method: paymentMethod,
        coupon_code: coupon.applied?.code || null,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success(t("common.toast.orderPlaced"));
      navigate(`/order-success/${data.id}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <h1 className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight">{t("checkout.title")}</h1>
      <div className="mt-2 text-sm font-mono text-slate-500">{t("checkout.cod")}</div>

      <form onSubmit={handleSubmit} className="mt-10 grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8 space-y-5">
          {savedAddresses.length > 0 && (
            <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
              <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-3">{t("checkout.savedAddresses")}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {savedAddresses.map((a, idx) => (
                  <button
                    type="button"
                    key={`${a.full_name}-${a.phone}-${a.city}-${idx}`}
                    onClick={() => setForm({ ...form, ...a, notes: form.notes })}
                    data-testid={`saved-address-${idx}`}
                    className="text-left p-4 rounded-xl bg-secondary hover:bg-ink hover:text-white transition-colors"
                  >
                    <div className="font-display font-bold text-sm">{a.full_name}</div>
                    <div className="text-xs font-mono mt-1 opacity-80">{a.phone}</div>
                    <div className="text-xs font-mono mt-1 opacity-80 truncate">{a.address}, {tCity(a.city)}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">{t("checkout.shipping")}</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">{t("checkout.fullName")}</label>
                <input required data-testid="checkout-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">{t("checkout.phone")}</label>
                <input required data-testid="checkout-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-mono uppercase tracking-widest">
                  {t("checkout.email")}
                  {!user && <span className="ml-2 text-electric normal-case tracking-normal">({t("checkout.forGuestReceipt")})</span>}
                </label>
                <input
                  type="email"
                  required={!user || user === false}
                  data-testid="checkout-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1 w-full h-11 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-mono uppercase tracking-widest">{t("checkout.address")}</label>
                <input required data-testid="checkout-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-widest">{t("checkout.city")}</label>
                <select data-testid="checkout-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-1 w-full h-11 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30">
                  {CITY_KEYS.map((c) => (
                    <option key={c} value={c}>{tCity(c)}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-mono uppercase tracking-widest">{t("checkout.notes")}</label>
                <textarea data-testid="checkout-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className="mt-1 w-full px-3 py-2 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)]">
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500 mb-4">{t("checkout.payment")}</div>
            <label
              data-testid="payment-installation"
              className="block rounded-xl p-4 border-2 border-ink bg-frost"
            >
              <div className="flex items-start gap-3">
                <Check size={18} className="text-electric mt-0.5 shrink-0" />
                <div>
                  <div className="font-display font-bold text-sm">{t("checkout.payInstallation")}</div>
                  <div className="mt-1 text-xs text-slate-600">{t("checkout.payInstallationSub")}</div>
                </div>
              </div>
            </label>
          </div>

          {!user && (
            <div className="bg-frost border border-electric/30 rounded-xl p-4 text-sm flex items-center justify-between gap-3 flex-wrap">
              <span className="text-slate-700">{t("checkout.guestTip")}</span>
              <Link
                to="/login"
                className="text-[11px] font-mono uppercase tracking-widest text-electric hover:underline"
              >
                {t("auth.signIn")} →
              </Link>
            </div>
          )}
        </div>

        <aside className="lg:col-span-4">
          <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_-8px_rgba(0,0,0,0.08)] sticky top-24">
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-slate-500">{t("checkout.summary")}</div>
            <div className="mt-4 space-y-2 max-h-[180px] overflow-auto pr-2">
              {items.map((i) => (
                <div key={`${i.product_id}_${i.variant_id || "novariant"}`} className="flex items-center justify-between text-sm">
                  <span className="truncate">
                    {i.name}
                    {i.variant_label && <span className="text-electric ml-1">· {i.variant_label}</span>}
                    <span className="text-slate-500"> ×{i.quantity}</span>
                  </span>
                  <span className="font-mono">{formatJOD(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>

            {/* Coupon */}
            <div className="mt-6">
              <div className="text-xs font-mono uppercase tracking-widest mb-2 flex items-center gap-1.5"><Tag size={12} /> {t("checkout.promoCode")}</div>
              {coupon.applied ? (
                <div className="flex items-center justify-between gap-2 bg-electric/10 text-electric px-3 h-11 rounded-lg">
                  <span className="font-mono font-bold text-sm">{coupon.applied.code}</span>
                  <span className="font-mono text-sm">−{formatJOD(coupon.applied.discount)}</span>
                  <button type="button" onClick={removeCoupon} className="w-7 h-7 grid place-items-center rounded-full hover:bg-electric hover:text-white"><X size={12} /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={coupon.code}
                    onChange={(e) => setCoupon({ ...coupon, code: e.target.value, error: "" })}
                    placeholder="SHAMS10"
                    data-testid="coupon-input"
                    className="flex-1 h-11 px-3 rounded-lg bg-secondary focus:outline-none focus:ring-2 focus:ring-electric/30 font-mono uppercase text-sm"
                  />
                  <button type="button" onClick={applyCoupon} data-testid="coupon-apply" className="h-11 px-4 rounded-lg bg-ink text-white text-xs font-mono uppercase tracking-widest hover:bg-electric">{t("checkout.applyCoupon")}</button>
                </div>
              )}
              {coupon.error && <div className="mt-2 text-xs font-mono text-destructive">{coupon.error}</div>}
            </div>

            <div className="mt-6 space-y-2 text-sm font-mono">
              <div className="flex justify-between"><span>{t("common.subtotal")}</span><span>{formatJOD(subtotal)}</span></div>
              {discount > 0 && (
                <div className="flex justify-between text-electric"><span>{t("common.discount")}</span><span>− {formatJOD(discount)}</span></div>
              )}
              <div className="pt-2 border-t border-black/10 flex items-center justify-between">
                <span className="font-display font-bold">{t("common.total")}</span>
                <span className="font-display font-black text-2xl">{formatJOD(total)}</span>
              </div>
            </div>

            {error && <div className="mt-4 text-sm font-mono text-destructive">{error}</div>}

            <button
              type="submit"
              disabled={submitting}
              data-testid="checkout-submit"
              className="mt-6 w-full h-12 rounded-full bg-ink text-white hover:bg-electric disabled:opacity-50 transition-colors text-xs font-mono uppercase tracking-[0.2em]"
            >
              {submitting ? t("checkout.placing") : t("checkout.placeOrder")}
            </button>
          </div>
        </aside>
      </form>
    </div>
  );
}
